import React, { useState, useEffect, useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import { useNavigate } from 'react-router-dom';
import {
  FunnelIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  Squares2X2Icon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  BuildingOffice2Icon,
  UserCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import TransactionProofImage from './TransactionProofImage';

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v))
    : '₹0';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/* ─────────────────────────────────────────────
   ATOMS
───────────────────────────────────────────── */
const TypeBadge = ({ type }) =>
  type === 'credit' ? (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
      ↑ Credit
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
      ↓ Debit
    </span>
  );

const StatusBadge = ({ status }) => {
  const s = (status || 'RECEIVED').toUpperCase();
  const styles = {
    SUBMITTED: 'bg-blue-50 text-blue-700 border-blue-200',
    SUBMIT:    'bg-blue-50 text-blue-700 border-blue-200',
    SAVED:     'bg-amber-50 text-amber-700 border-amber-200',
    SAVE:      'bg-amber-50 text-amber-700 border-amber-200',
    RECEIVED:  'bg-slate-50 text-slate-600 border-slate-200',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${styles[s] || styles.RECEIVED}`}>{s}</span>
  );
};

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <ChevronUpDownIcon className="w-3 h-3 opacity-40" />;
  return sortConfig.direction === 'asc'
    ? <ChevronUpIcon className="w-3 h-3 text-indigo-400" />
    : <ChevronDownIcon className="w-3 h-3 text-indigo-400" />;
};

/* ─────────────────────────────────────────────
   TRANSACTION CARD (mobile)
───────────────────────────────────────────── */
const TxnCard = ({ txn, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(index * 0.025, 0.25) }}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform duration-75"
  >
    <div className={`h-0.5 w-full ${txn.amount_type === 'credit' ? 'bg-emerald-400' : 'bg-red-400'}`} />
    <div className="px-4 py-3.5">
      {/* Row 1: date + type + amount */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium text-slate-400">{fmtDate(txn.transaction_date)}</span>
          <TypeBadge type={txn.amount_type} />
        </div>
        <p className={`text-[15px] font-bold tabular-nums ${txn.amount_type === 'credit' ? 'text-emerald-600' : 'text-red-600'}`}>
          {txn.amount_type === 'credit' ? '+' : '-'}{fmt(txn.amount_type === 'credit' ? txn.credit_amount : txn.debit_amount)}
        </p>
      </div>
      {/* Row 2: party */}
      <p className="text-[14px] font-bold text-slate-800 leading-snug">{txn.particuler_to || '—'}</p>
      {/* Row 3: tags */}
      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
        {txn.main_head && (
          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100">{txn.main_head}</span>
        )}
        {txn.sub_head && (
          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-semibold rounded-full">{txn.sub_head}</span>
        )}
        {txn.site && (
          <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
            <BuildingOffice2Icon className="w-3 h-3" />{txn.site}
          </span>
        )}
      </div>
      {/* Row 4: footer */}
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-50">
        <div className="flex items-center gap-2">
          <StatusBadge status={txn.form_status} />
          <TransactionProofImage path={txn.attachment_path} />
          {txn.created_by && (
            <span className="text-[10px] text-slate-400">by {txn.created_by}</span>
          )}
        </div>
        <span className="text-[10px] text-slate-400 tabular-nums">Bal {fmt(txn.net_balance)}</span>
      </div>
      {txn.remarks && (
        <p className="mt-1.5 text-[10px] text-slate-400 italic truncate">{txn.remarks}</p>
      )}
    </div>
  </motion.div>
);

/* ─────────────────────────────────────────────
   DESKTOP TABLE
───────────────────────────────────────────── */
const TxnTable = ({ rows, sortConfig, onSort }) => {
  const cols = [
    { key: 'sno',              label: '#',          w: 'w-8' },
    { key: 'transaction_date', label: 'Date',       w: 'w-24' },
    { key: 'amount_type',      label: 'Type',       w: 'w-20' },
    { key: 'credit_amount',    label: 'Credit',     w: 'w-24' },
    { key: 'debit_amount',     label: 'Debit',      w: 'w-24' },
    { key: 'net_balance',      label: 'Balance',    w: 'w-24' },
    { key: 'particuler_to',    label: 'Particulars',w: 'w-28' },
    { key: 'created_by',       label: 'By',         w: 'w-20' },
    { key: 'site',             label: 'Site',       w: 'w-20' },
    { key: 'project_name',     label: 'Project',    w: 'w-24' },
    { key: 'main_head',        label: 'Main Head',  w: 'w-24' },
    { key: 'sub_head',         label: 'Sub Head',   w: 'w-24' },
    { key: 'remarks',          label: 'Remarks',    w: 'w-28' },
    { key: 'proof',            label: 'Proof',      w: 'w-20' },
    { key: 'form_status',      label: 'Status',     w: 'w-20' },
    { key: 'created_at',       label: 'Created',    w: 'w-32' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[11px] border-collapse" style={{ minWidth: 1080 }}>
        <thead>
          <tr className="bg-slate-900 text-white">
            {cols.map((col, i) => (
              <th
                key={col.key}
                onClick={() => col.key !== 'sno' && onSort(col.key)}
                className={`px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap select-none
                  ${col.key !== 'sno' ? 'cursor-pointer hover:bg-slate-800' : ''}
                  ${i === 0 ? 'rounded-tl-lg' : ''}
                  ${i === cols.length - 1 ? 'rounded-tr-lg' : ''}
                  transition-colors`}
              >
                <span className="flex items-center gap-1">
                  {col.label}
                  {col.key !== 'sno' && <SortIcon col={col.key} sortConfig={sortConfig} />}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-[12px] text-slate-400 italic">No transactions found</td></tr>
          )}
          {rows.map((txn, i) => (
            <motion.tr
              key={`${txn.transaction_no || i}-${i}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: Math.min(i * 0.015, 0.2) }}
              className="hover:bg-slate-50/70 transition-colors group"
            >
              <td className="px-3 py-2.5 text-slate-400 font-medium">{i + 1}</td>
              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap font-medium">{fmtDate(txn.transaction_date)}</td>
              <td className="px-3 py-2.5"><TypeBadge type={txn.amount_type} /></td>
              <td className="px-3 py-2.5 text-emerald-600 font-semibold tabular-nums">
                {txn.amount_type === 'credit' ? fmt(txn.credit_amount) : '—'}
              </td>
              <td className="px-3 py-2.5 text-red-500 font-semibold tabular-nums">
                {txn.amount_type === 'debit' ? fmt(txn.debit_amount) : '—'}
              </td>
              <td className="px-3 py-2.5 text-slate-800 font-bold tabular-nums">{fmt(txn.net_balance)}</td>
              <td className="px-3 py-2.5 text-slate-700 max-w-[100px] truncate">{txn.particuler_to || '—'}</td>
              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.created_by || '—'}</td>
              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.site || '—'}</td>
              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.project_name || '—'}</td>
              <td className="px-3 py-2.5">
                {txn.main_head
                  ? <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100 whitespace-nowrap">{txn.main_head}</span>
                  : '—'}
              </td>
              <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{txn.sub_head || '—'}</td>
              <td className="px-3 py-2.5 text-slate-400 max-w-[100px] truncate">{txn.remarks || '—'}</td>
              <td className="px-3 py-2.5"><TransactionProofImage path={txn.attachment_path} /></td>
              <td className="px-3 py-2.5"><StatusBadge status={txn.form_status} /></td>
              <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{fmtDateTime(txn.created_at)}</td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────────────────────────────────────
   USER SECTION BLOCK
───────────────────────────────────────────── */
const UserSection = ({ userName, transactions, viewMode, sortConfig, onSort, isAdmin = false }) => {
  const [collapsed, setCollapsed] = useState(false);

  const credit = transactions.filter(t => t.amount_type === 'credit').reduce((s, t) => s + Number(t.credit_amount || 0), 0);
  const debit  = transactions.filter(t => t.amount_type === 'debit').reduce((s, t) => s + Number(t.debit_amount || 0), 0);
  const bal    = transactions.length > 0 ? Number(transactions[transactions.length - 1]?.net_balance || 0) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-4"
    >
      {/* Section accent */}
      <div className={`h-0.5 w-full ${isAdmin ? 'bg-violet-400' : 'bg-indigo-400'}`} />

      {/* Section header — tappable to collapse */}
      <button
        onClick={() => setCollapsed(p => !p)}
        className="w-full flex items-center justify-between px-4 py-3.5 touch-manipulation group"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-violet-100' : 'bg-indigo-100'}`}>
            {isAdmin
              ? <ShieldCheckIcon className={`w-4 h-4 ${isAdmin ? 'text-violet-600' : 'text-indigo-600'}`} />
              : <UserCircleIcon className="w-4 h-4 text-indigo-600" />}
          </div>
          <div className="text-left">
            <p className="text-[13px] font-bold text-slate-800 leading-tight">{userName}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{transactions.length} transactions</p>
          </div>
        </div>
        {/* Mini summary */}
        <div className="hidden sm:flex items-center gap-4 mr-3">
          <div className="text-right">
            <p className="text-[9px] text-slate-400 uppercase font-bold">Credit</p>
            <p className="text-[12px] text-emerald-600 font-bold tabular-nums">{fmt(credit)}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-slate-400 uppercase font-bold">Debit</p>
            <p className="text-[12px] text-red-500 font-bold tabular-nums">{fmt(debit)}</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-slate-400 uppercase font-bold">Balance</p>
            <p className={`text-[12px] font-bold tabular-nums ${bal >= 0 ? 'text-slate-900' : 'text-red-600'}`}>{fmt(bal)}</p>
          </div>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
      </button>

      {/* Mobile mini summary */}
      <div className="sm:hidden flex items-center gap-4 px-4 pb-3 -mt-1">
        {[
          { label: 'Credit', val: fmt(credit), color: 'text-emerald-600' },
          { label: 'Debit',  val: fmt(debit),  color: 'text-red-500'     },
          { label: 'Balance',val: fmt(bal),     color: bal >= 0 ? 'text-slate-900' : 'text-red-600' },
        ].map(({ label, val, color }) => (
          <div key={label}>
            <p className="text-[9px] text-slate-400 uppercase font-bold">{label}</p>
            <p className={`text-[11px] font-bold tabular-nums ${color}`}>{val}</p>
          </div>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden border-t border-slate-100"
          >
            {/* Card view */}
            {viewMode === 'cards' && (
              <div className="p-3 space-y-2.5 max-h-[420px] overflow-y-auto">
                {transactions.map((txn, idx) => (
                  <TxnCard key={`${txn.transaction_no || idx}-${idx}`} txn={txn} index={idx} />
                ))}
              </div>
            )}
            {/* Table view */}
            {viewMode === 'table' && (
              <div className="max-h-[480px] overflow-auto">
                <TxnTable rows={transactions} sortConfig={sortConfig} onSort={onSort} />
                <div className="px-4 py-2.5 border-t border-slate-50 bg-slate-50/40">
                  <p className="text-[10px] text-slate-400">
                    <span className="font-bold text-slate-600">{transactions.length}</span> entries
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const DetailedTransactionReport = () => {
  const [allTransactions, setAllTransactions]         = useState([]);
  const [adminTransactions, setAdminTransactions]     = useState([]);
  const [filteredTransactions, setFilteredTransactions]           = useState([]);
  const [filteredAdminTransactions, setFilteredAdminTransactions] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);
  const [filterOpen, setFilterOpen]         = useState(false);
  const [viewMode, setViewMode]             = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table'
  );
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '', userName: '', site: '', project: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'asc' });

  const admin_id = localStorage.getItem('id');
  const role     = localStorage.getItem('role');
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);

  /* fetch */
  const fetchTransactions = async () => {
    if (!['admin', 'company_owner'].includes(role) || !admin_id) { setError('Access denied'); return; }
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ admin_id, role });
      if (role === 'company_owner' && filters.site) params.append('site', filters.site);
      const res  = await fetch(`${ApiURL}/getDetailedTransactionReportWithAllAdmin.php?${params}`);
      const data = await res.json();
      if (data.status === 'success') {
        setAllTransactions(data.transactions);
        setFilteredTransactions(data.transactions);
        setAdminTransactions(data.admin_transactions);
        setFilteredAdminTransactions(data.admin_transactions);
        setAvailableSites(Array.isArray(data.site_options) ? data.site_options : []);
        setError(null);
      } else setError(data.message);
    } catch (e) { setError('Failed to fetch: ' + e.message); }
    finally     { setIsLoading(false); }
  };

  useEffect(() => { fetchTransactions(); }, [admin_id, role, filters.site]);

  /* client-side filters */
  useEffect(() => {
    const apply = (list) => {
      let r = [...list];
      if (filters.startDate) r = r.filter(t => new Date(t.transaction_date) >= new Date(filters.startDate));
      if (filters.endDate)   r = r.filter(t => new Date(t.transaction_date) <= new Date(filters.endDate));
      if (filters.status)    r = r.filter(t => (t.form_status || 'RECEIVED') === filters.status);
      if (filters.userName)  {
        const q = filters.userName.toLowerCase();
        r = r.filter(t =>
          (t.particuler_to||'').toLowerCase().includes(q) ||
          (t.created_by||'').toLowerCase().includes(q) ||
          (t.project_name||'').toLowerCase().includes(q) ||
          (t.user_name||'').toLowerCase().includes(q)
        );
      }
      if (filters.site) r = r.filter(t => (t.site||'') === filters.site);
      if (filters.project) r = r.filter(t => (t.project_name||'') === filters.project);
      return r;
    };
    setFilteredTransactions(apply(allTransactions));
    setFilteredAdminTransactions(apply(adminTransactions));
  }, [filters, allTransactions, adminTransactions]);

  /* sort */
  const handleSort = (key) => setSortConfig(p => ({
    key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc'
  }));

  const sortTxns = (list) => {
    if (!sortConfig.key || sortConfig.key === 'sno') return list;
    return [...list].sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      const av  = a[sortConfig.key] || '';
      const bv  = b[sortConfig.key] || '';
      if (['created_at','transaction_date'].includes(sortConfig.key))
        return dir * (new Date(av||0) - new Date(bv||0));
      if (['credit_amount','debit_amount','net_balance'].includes(sortConfig.key))
        return dir * (parseFloat(av||0) - parseFloat(bv||0));
      return dir * av.toString().localeCompare(bv.toString());
    });
  };

  const groupByUser = (list) => {
    const g = {};
    list.forEach(t => { const u = t.user_name||'Unknown'; if (!g[u]) g[u]=[]; g[u].push(t); });
    Object.keys(g).forEach(u => { g[u] = sortTxns(g[u]); });
    return g;
  };

  const groupedAdmin = groupByUser(sortTxns(filteredAdminTransactions));
  const groupedUsers = groupByUser(sortTxns(filteredTransactions));

  /* export */
  const exportCSV = () => {
    const headers = ['Section','User','#','Date','Type','Credit','Debit','Balance','Particulars','By','Site','Project','Main Head','Sub Head','Remarks','Status','Created At'];
    const makeRow = (section, txn, idx) => [
      section, txn.user_name||'', idx+1, txn.transaction_date||'',
      txn.amount_type||'',
      parseFloat(txn.credit_amount||0).toFixed(2),
      parseFloat(txn.debit_amount||0).toFixed(2),
      parseFloat(txn.net_balance||0).toFixed(2),
      txn.particuler_to||'', txn.created_by||'', txn.site||'', txn.project_name||'',
      txn.main_head||'', txn.sub_head||'', txn.remarks||'',
      txn.form_status||'', txn.created_at||'',
    ].map(v => { const s=String(v??''); return s.includes(',')||s.includes('"') ? `"${s.replace(/"/g,'""')}"` : s; });

    const rows = [headers];
    sortTxns(filteredAdminTransactions).forEach((t,i) => rows.push(makeRow('Admin',t,i)));
    Object.entries(groupedUsers).forEach(([u,txns]) => txns.forEach((t,i) => rows.push(makeRow(`User-${u}`,t,i))));
    const blob = new Blob([rows.map(r=>r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = 'detailed_transaction_report.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const projectOptions = useMemo(() => {
    const all = [...allTransactions, ...adminTransactions].map(t => t.project_name).filter(Boolean);
    return [...new Set(all)].sort();
  }, [allTransactions, adminTransactions]);
  const hasFilter = filters.startDate || filters.endDate || filters.status || filters.userName || filters.site || filters.project;
  const totalAdmin = filteredAdminTransactions.length;
  const totalUser  = filteredTransactions.length;

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6 pb-12">

            {/* ── HERO HEADER ── */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-950 rounded-2xl overflow-hidden shadow-lg mb-4"
            >
              <div className="h-0.5 bg-gradient-to-r from-indigo-400 via-indigo-300 to-transparent" />
              <div className="px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-indigo-400 mb-1">Reports</p>
                    <h1 className="text-lg sm:text-2xl font-bold text-white leading-tight">Detailed Transaction Report</h1>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {totalAdmin} admin · {totalUser} user transactions
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* View toggle */}
                    <div className="flex items-center bg-white/10 border border-white/20 rounded-xl p-0.5">
                      <button
                        onClick={() => setViewMode('table')}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all touch-manipulation ${viewMode === 'table' ? 'bg-indigo-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                        aria-label="Table view"
                      >
                        <TableCellsIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('cards')}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all touch-manipulation ${viewMode === 'cards' ? 'bg-indigo-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                        aria-label="Card view"
                      >
                        <Squares2X2Icon className="w-4 h-4" />
                      </button>
                    </div>
                    {/* Filter */}
                    <button
                      onClick={() => setFilterOpen(p => !p)}
                      className={`flex items-center gap-1.5 px-3 h-9 rounded-xl border text-[11px] font-bold transition-all touch-manipulation ${
                        hasFilter ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                      }`}
                    >
                      <FunnelIcon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Filter</span>
                      {hasFilter && <span className="w-1.5 h-1.5 rounded-full bg-white/80" />}
                    </button>
                    {/* Export */}
                    <button
                      onClick={exportCSV}
                      className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white text-[11px] font-bold transition-all touch-manipulation"
                    >
                      <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Export</span>
                    </button>
                    {/* Refresh */}
                    <button
                      onClick={fetchTransactions}
                      className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition-all touch-manipulation"
                    >
                      <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ── FILTER PANEL ── */}
            <AnimatePresence>
              {filterOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Filters</p>
                      {hasFilter && (
                        <button
                          onClick={() => setFilters({ startDate:'', endDate:'', status:'', userName:'', site:'', project:'' })}
                          className="text-[11px] text-rose-500 font-bold hover:text-rose-600 touch-manipulation"
                        >Clear all</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* From */}
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">From</label>
                        <div className="relative">
                          <CalendarDaysIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                          <input type="date" value={filters.startDate}
                            onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))}
                            className="w-full h-9 pl-8 pr-2 text-[11px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" />
                        </div>
                      </div>
                      {/* To */}
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">To</label>
                        <div className="relative">
                          <CalendarDaysIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                          <input type="date" value={filters.endDate}
                            onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))}
                            className="w-full h-9 pl-8 pr-2 text-[11px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" />
                        </div>
                      </div>
                      {/* Status */}
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Status</label>
                        <select value={filters.status}
                          onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}
                          className="w-full h-9 px-3 text-[11px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all appearance-none"
                        >
                          <option value="">All Status</option>
                          <option value="SUBMIT">Submit</option>
                          <option value="RECEIVED">Received</option>
                          <option value="SAVE">Save</option>
                        </select>
                      </div>
                      {/* Search */}
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Search User</label>
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                          <input type="text" value={filters.userName} placeholder="Name…"
                            onChange={e => setFilters(p => ({ ...p, userName: e.target.value }))}
                            className="w-full h-9 pl-8 pr-8 text-[11px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all" />
                          {filters.userName && (
                            <button onClick={() => setFilters(p => ({ ...p, userName:'' }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 touch-manipulation">
                              <XMarkIcon className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Site */}
                      {role === 'company_owner' && (
                        <div>
                          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Site</label>
                          <div className="relative">
                            <BuildingOffice2Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                            <select value={filters.site}
                              onChange={e => setFilters(p => ({ ...p, site: e.target.value }))}
                              className="w-full h-9 pl-8 pr-2 text-[11px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all appearance-none"
                            >
                              <option value="">All Sites</option>
                              {availableSites.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Project</label>
                        <select value={filters.project}
                          onChange={e => setFilters(p => ({ ...p, project: e.target.value }))}
                          className="w-full h-9 px-3 text-[11px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all appearance-none"
                        >
                          <option value="">All Projects</option>
                          {projectOptions.map(project => <option key={project} value={project}>{project}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── ERROR ── */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-[12px] rounded-xl px-4 py-3">{error}</div>
            )}

            {/* ── SKELETON ── */}
            {isLoading && (
              <div className="space-y-3">
                {[...Array(3)].map((_,i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-100 h-20 animate-pulse" />
                ))}
              </div>
            )}

            {!isLoading && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>

                {/* ── ADMIN TRANSACTIONS ── */}
                <div className="mb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-5 bg-violet-400 rounded-full" />
                    <h2 className="text-[12px] font-bold text-slate-600 uppercase tracking-widest">Admin Transactions</h2>
                    <span className="px-2 py-0.5 bg-violet-50 text-violet-600 text-[10px] font-bold rounded-full border border-violet-200">{totalAdmin}</span>
                  </div>

                  {totalAdmin === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-100 py-10 text-center text-[12px] text-slate-400 italic shadow-sm">
                      No admin transactions found
                    </div>
                  ) : role === 'company_owner' ? (
                    Object.entries(groupedAdmin).map(([user, txns]) => (
                      <UserSection key={user} userName={user} transactions={txns} viewMode={viewMode} sortConfig={sortConfig} onSort={handleSort} isAdmin />
                    ))
                  ) : (
                    <UserSection
                      userName={`Admin (${Object.keys(groupedAdmin)[0] || 'All'})`}
                      transactions={sortTxns(filteredAdminTransactions)}
                      viewMode={viewMode} sortConfig={sortConfig} onSort={handleSort} isAdmin
                    />
                  )}
                </div>

                {/* ── USER TRANSACTIONS ── */}
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-5 bg-indigo-400 rounded-full" />
                    <h2 className="text-[12px] font-bold text-slate-600 uppercase tracking-widest">User Transactions</h2>
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full border border-indigo-200">{totalUser}</span>
                  </div>

                  {Object.keys(groupedUsers).length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-100 py-10 text-center text-[12px] text-slate-400 italic shadow-sm">
                      No user transactions found
                    </div>
                  ) : (
                    Object.entries(groupedUsers).map(([user, txns]) => (
                      <UserSection key={user} userName={user} transactions={txns} viewMode={viewMode} sortConfig={sortConfig} onSort={handleSort} />
                    ))
                  )}
                </div>

                {/* Bottom export */}
                <div className="flex justify-center mt-6">
                  <button
                    onClick={exportCSV}
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-50 text-indigo-700 text-[12px] font-bold rounded-xl hover:bg-indigo-100 active:bg-indigo-200 transition-colors touch-manipulation border border-indigo-200"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4" />
                    Export Full Report CSV
                  </button>
                </div>

              </motion.div>
            )}
          </div>
        </motion.main>
      </div>

      {isSidebarOpen && screenWidth < 640 && (
        <div className="fixed inset-0 z-30 bg-black/50" aria-hidden="true" />
      )}
    </div>
  );
};

export default DetailedTransactionReport;
